import React, {useState} from "react";
import {toDate} from "date-fns";
import {useSelector} from "react-redux";

import {styled, Button, DATE_FORMAT} from "@reside/ui";

import {AppState, select} from "../../../../../store";
import {
  AdmissionId,
  CellFooter,
  CellIdentityBlock,
  CellLastUpdatePrefix,
  CellLastUpdateRow,
  CellLastUpdateTime,
  CellResidentNameLink,
  DashboardCell,
  Left,
  Right,
} from "../../../../dashboard-cell";
import {WorkflowRibbon} from "../../../../dashboard-cell/WorkflowRibbon";
import {AdmissionFromSearch} from "../../../../../services/AdmissionsService";
import {RelativeDateTime} from "../../../../relative-date-time";
import {getAdmissionResidentName, getResidentTestId} from "../../../helper";
import {StarAdmissionIcon} from "../../../../star-icon/StarAdmissionIcon";
import {SetWorkflowModal} from "../../../../set-workflow/SetWorkflowModal";
import {PccAdmittedActions} from "../PccAdmittedActions";

type Props = Readonly<{
  admission: AdmissionFromSearch;
}>;

/**
 * Preflight card for an admission whose workflow hasn't been resolved yet (synced from PCC
 * against a template that declares multiple workflows). Delete/Force Delete remain available via
 * the kebab menu; no other card actions are offered until the workflow is set — the backend
 * rejects every other mutation while pending.
 */
export const PendingWorkflowPreflightCell = ({admission}: Props) => {
  const {updated, code} = admission;
  const updatedAt = toDate(updated * 1000);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isResideAdmin = useSelector((state: AppState) =>
    select.adminSession.isResideAdmin(state),
  );

  return (
    <DashboardCell>
      <WorkflowRibbon
        workflowType={admission.workflowType}
        star={
          <StarAdmissionIcon
            workflowType={admission.workflowType}
            admissionId={admission.id}
            isStarred={admission.starred}
          />
        }
        actions={<PccAdmittedActions admission={admission} />}
      />
      <Row>
        <CellIdentityBlock>
          <ResidentName admission={admission} />
          <AdmissionId code={code} />
        </CellIdentityBlock>
      </Row>
      <CellFooter>
        <Left>
          <CellLastUpdateRow>
            <CellLastUpdatePrefix>Last update:</CellLastUpdatePrefix>
            <CellLastUpdateTime>
              <RelativeDateTime
                dateTime={updatedAt}
                dateFormat={DATE_FORMAT.DAY_IN_MONTH}
              />
            </CellLastUpdateTime>
          </CellLastUpdateRow>
        </Left>
        <Right>
          <Button
            size="small"
            color="primary"
            outline
            flat
            disabled={isResideAdmin}
            onClick={() => {
              if (!isResideAdmin) {
                setIsModalOpen(true);
              }
            }}
            testId={getResidentTestId(admission, "setWorkflow")}
          >
            Set workflow
          </Button>
        </Right>
      </CellFooter>
      <SetWorkflowModal
        admission={admission}
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
      />
    </DashboardCell>
  );
};

const ResidentName = ({admission}: Props) => (
  <CellResidentNameLink
    name={getAdmissionResidentName(admission)}
    data-test-id={getResidentTestId(admission, "noAction")}
    style={{display: "flex"}}
  />
);

const Row = styled.div`
  display: flex;
`;
